// src/components/Admin/EditorialCuration.js
import React, { useState, useEffect, useCallback } from 'react';

const EditorialCuration = () => {
  const [editorials, setEditorials] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activeTab, setActiveTab] = useState('drafts'); // 'drafts', 'published', 'scheduled'
  const [sortBy, setSortBy] = useState('newest');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const [newEditorial, setNewEditorial] = useState({
    title: '',
    excerpt: '',
    content: '',
    author: '',
    category: 'design',
    featured: false,
    publishDate: '',
    status: 'draft'
  });

  const categories = ['design', 'architecture', 'interiors', 'art', 'culture', 'lifestyle'];
  const statuses = ['draft', 'review', 'published', 'scheduled', 'archived'];

  // Mock editorial data - in reality would come from database
  const mockEditorials = [
    {
      id: 'edit_001',
      title: 'The Return of Brutalist Architecture in Modern Design',
      excerpt: 'Exploring how brutalist elements are making a comeback in contemporary architecture.',
      author: 'Sarah Chen',
      category: 'architecture',
      status: 'draft',
      featured: false,
      createdAt: Date.now() - 86400000, // 1 day ago
      publishDate: null,
      wordCount: 1250
    },
    {
      id: 'edit_002',
      title: 'Minimalism vs. Maximalism: Finding Balance in Interior Design',
      excerpt: 'A deep dive into contrasting design philosophies and how to find your aesthetic.',
      author: 'Marcus Thompson',
      category: 'interiors',
      status: 'review',
      featured: true,
      createdAt: Date.now() - 172800000, // 2 days ago
      publishDate: Date.now() + 86400000, // Tomorrow
      wordCount: 890
    },
    {
      id: 'edit_003',
      title: 'Digital Art Meets Physical Spaces: The Future of Gallery Design',
      excerpt: 'How technology is reshaping the way we experience art in physical spaces.',
      author: 'Elena Rodriguez',
      category: 'art',
      status: 'published',
      featured: false,
      createdAt: Date.now() - 259200000, // 3 days ago
      publishDate: Date.now() - 86400000, // Yesterday
      wordCount: 2100
    }
  ];

  // Load editorial data
  const loadEditorials = useCallback(async () => {
    setIsLoading(true);
    try {
      // In reality, this would fetch from your backend
      await new Promise(resolve => setTimeout(resolve, 500)); // Simulate API call
      setEditorials(mockEditorials);
      setMessage(`✅ Loaded ${mockEditorials.length} editorials`);
    } catch (error) {
      console.error('Error loading editorials:', error);
      setMessage('❌ Error loading editorial data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEditorials();
  }, [loadEditorials]);

  // Filter and sort editorials
  const getFilteredEditorials = useCallback(() => {
    let filtered = [...editorials];
    
    // Filter by tab
    switch (activeTab) {
      case 'drafts':
        filtered = filtered.filter(item => ['draft', 'review'].includes(item.status));
        break;
      case 'published':
        filtered = filtered.filter(item => item.status === 'published');
        break;
      case 'scheduled':
        filtered = filtered.filter(item => item.status === 'scheduled');
        break;
    }
    
    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter(item => item.status === statusFilter);
    }
    
    // Apply sorting
    const sorted = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.createdAt || 0) - (a.createdAt || 0);
        case 'oldest':
          return (a.createdAt || 0) - (b.createdAt || 0);
        case 'title':
          return (a.title || '').localeCompare(b.title || '');
        case 'author':
          return (a.author || '').localeCompare(b.author || '');
        case 'publishDate':
          return (b.publishDate || 0) - (a.publishDate || 0);
        default:
          return 0;
      }
    });
    
    return sorted;
  }, [editorials, activeTab, statusFilter, sortBy]);

  const filteredEditorials = getFilteredEditorials();

  // Handle editorial actions
  const handlePublishItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Publish ${itemIds.length} editorials?`)) {
      return;
    }

    setIsLoading(true);
    try {
      // Mock publishing logic
      console.log('Publishing editorials:', itemIds);
      setMessage(`✅ Would publish ${itemIds.length} editorials (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error publishing editorials:', error);
      setMessage('❌ Error publishing editorials');
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchiveItems = async (itemIds) => {
    if (itemIds.length === 0) {
      setMessage('❌ No items selected');
      return;
    }

    if (!window.confirm(`Archive ${itemIds.length} editorials?`)) {
      return;
    }

    setIsLoading(true);
    try {
      console.log('Archiving editorials:', itemIds);
      setMessage(`✅ Would archive ${itemIds.length} editorials (backend API needed)`);
      setSelectedItems(new Set());
    } catch (error) {
      console.error('Error archiving editorials:', error);
      setMessage('❌ Error archiving editorials');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle item selection
  const handleItemSelect = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedItems.size === filteredEditorials.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredEditorials.map(item => item.id)));
    }
  };

  // Handle new editorial form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewEditorial(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmitNewEditorial = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const editorial = {
        ...newEditorial,
        id: `edit_${Date.now()}`,
        createdAt: Date.now(),
        wordCount: newEditorial.content.split(' ').length
      };
      
      console.log('Creating new editorial:', editorial);
      setMessage('✅ Editorial created successfully (backend API needed)');
      setShowCreateForm(false);
      setNewEditorial({
        title: '',
        excerpt: '',
        content: '',
        author: '',
        category: 'design',
        featured: false,
        publishDate: '',
        status: 'draft'
      });
      
    } catch (error) {
      console.error('Error creating editorial:', error);
      setMessage('❌ Error creating editorial');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#6c757d';
      case 'review': return '#ffc107';
      case 'published': return '#28a745';
      case 'scheduled': return '#17a2b8';
      case 'archived': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return '📝';
      case 'review': return '👀';
      case 'published': return '✅';
      case 'scheduled': return '⏰';
      case 'archived': return '📦';
      default: return '📄';
    }
  };

  return (
    <div style={{
      maxWidth: '1400px',
      margin: '2rem auto',
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>Editorial Curation</h2>
          <p style={{ margin: '0.5rem 0 0', color: '#666', fontSize: '0.9rem' }}>
            Manage editorial content and publishing schedule
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {/* Navigation to other admin tools */}
          <button
            onClick={() => window.location.href = '/admin'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            ← Admin Dashboard
          </button>
          
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: showCreateForm ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            {showCreateForm ? '❌ Cancel' : '✍️ New Editorial'}
          </button>
          
          <button
            onClick={loadEditorials}
            disabled={isLoading}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem'
            }}
          >
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Create Editorial Form */}
      {showCreateForm && (
        <div style={{
          marginBottom: '2rem',
          padding: '2rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#333' }}>Create New Editorial</h3>
          
          <form onSubmit={handleSubmitNewEditorial}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={newEditorial.title}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Author *
                </label>
                <input
                  type="text"
                  name="author"
                  value={newEditorial.author}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Excerpt
              </label>
              <textarea
                name="excerpt"
                value={newEditorial.excerpt}
                onChange={handleInputChange}
                rows="2"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                Content *
              </label>
              <textarea
                name="content"
                value={newEditorial.content}
                onChange={handleInputChange}
                required
                rows="8"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Category
                </label>
                <select
                  name="category"
                  value={newEditorial.category}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Status
                </label>
                <select
                  name="status"
                  value={newEditorial.status}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                >
                  {statuses.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600' }}>
                  Publish Date
                </label>
                <input
                  type="datetime-local"
                  name="publishDate"
                  value={newEditorial.publishDate}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="featured"
                  checked={newEditorial.featured}
                  onChange={handleInputChange}
                />
                <span style={{ fontWeight: '600' }}>Featured Editorial</span>
              </label>
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              style={{
                padding: '0.75rem 2rem',
                backgroundColor: isLoading ? '#ccc' : '#0d47a1',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Creating...' : 'Create Editorial'}
            </button>
          </form>
        </div>
      )}

      {/* Tabs */}
      <div style={{ 
        display: 'flex', 
        borderBottom: '2px solid #e9ecef',
        marginBottom: '2rem'
      }}>
        {[
          { id: 'drafts', label: 'Drafts & Review', count: editorials.filter(e => ['draft', 'review'].includes(e.status)).length },
          { id: 'published', label: 'Published', count: editorials.filter(e => e.status === 'published').length },
          { id: 'scheduled', label: 'Scheduled', count: editorials.filter(e => e.status === 'scheduled').length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '1rem 2rem',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #007bff' : '2px solid transparent',
              color: activeTab === tab.id ? '#007bff' : '#666',
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '600'
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem', 
        padding: '1rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Status Filter
          </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="">All Status</option>
            {statuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
        </div>
        
        <div style={{ flex: 1, minWidth: '150px' }}>
          <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.9rem', fontWeight: '600' }}>
            Sort By
          </label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              width: '100%',
              padding: '0.5rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              fontSize: '0.9rem'
            }}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="title">Title A-Z</option>
            <option value="author">Author A-Z</option>
            <option value="publishDate">Publish Date</option>
          </select>
        </div>
      </div>

      {/* Bulk Actions */}
      {activeTab === 'drafts' && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '1rem',
          padding: '1rem',
          backgroundColor: '#e9ecef',
          borderRadius: '6px'
        }}>
          <div>
            <button
              onClick={handleSelectAll}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.9rem',
                marginRight: '1rem'
              }}
            >
              {selectedItems.size === filteredEditorials.length ? 'Deselect All' : 'Select All'}
            </button>
            
            <span style={{ fontSize: '0.9rem', color: '#666' }}>
              {selectedItems.size} of {filteredEditorials.length} selected
            </span>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={() => handlePublishItems(Array.from(selectedItems))}
              disabled={selectedItems.size === 0 || isLoading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedItems.size > 0 ? '#28a745' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem'
              }}
            >
              📢 Publish Selected ({selectedItems.size})
            </button>
            
            <button
              onClick={() => handleArchiveItems(Array.from(selectedItems))}
              disabled={selectedItems.size === 0 || isLoading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: selectedItems.size > 0 ? '#dc3545' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: selectedItems.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.9rem'
              }}
            >
              📦 Archive Selected ({selectedItems.size})
            </button>
          </div>
        </div>
      )}

      {/* Editorial List */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', fontSize: '1.1rem', color: '#666' }}>
          Loading editorials...
        </div>
      ) : (
        <div>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))', 
            gap: '1.5rem' 
          }}>
            {filteredEditorials.map((editorial) => (
              <div key={editorial.id} style={{
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: 'white',
                position: 'relative',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                {/* Selection checkbox for drafts */}
                {activeTab === 'drafts' && (
                  <input
                    type="checkbox"
                    checked={selectedItems.has(editorial.id)}
                    onChange={() => handleItemSelect(editorial.id)}
                    style={{
                      position: 'absolute',
                      top: '1rem',
                      left: '1rem',
                      transform: 'scale(1.2)',
                      zIndex: 1
                    }}
                  />
                )}

                {/* Featured badge */}
                {editorial.featured && (
                  <div style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    backgroundColor: '#ffc107',
                    color: '#000',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.7rem',
                    fontWeight: 'bold'
                  }}>
                    ⭐ FEATURED
                  </div>
                )}
                
                {/* Editorial content */}
                <div style={{ marginTop: activeTab === 'drafts' ? '2rem' : '0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.2rem', marginRight: '0.5rem' }}>
                      {getStatusIcon(editorial.status)}
                    </span>
                    <span style={{
                      backgroundColor: getStatusColor(editorial.status),
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold'
                    }}>
                      {editorial.status.toUpperCase()}
                    </span>
                  </div>

                  <h3 style={{ 
                    margin: '0 0 0.5rem 0', 
                    fontSize: '1.2rem', 
                    fontWeight: '600',
                    lineHeight: '1.3'
                  }}>
                    {editorial.title}
                  </h3>
                  
                  {editorial.excerpt && (
                    <p style={{ 
                      margin: '0 0 0.75rem 0', 
                      fontSize: '0.9rem', 
                      color: '#666',
                      lineHeight: '1.4'
                    }}>
                      {editorial.excerpt}
                    </p>
                  )}
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>Author:</strong> {editorial.author}
                  </div>
                  
                  <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: '0.5rem' }}>
                    <strong>Category:</strong> {editorial.category} • <strong>Words:</strong> {editorial.wordCount}
                  </div>
                  
                  <div style={{ fontSize: '0.75rem', color: '#aaa', marginBottom: '1rem' }}>
                    Created: {new Date(editorial.createdAt).toLocaleDateString()}
                    {editorial.publishDate && (
                      <span> • Publish: {new Date(editorial.publishDate).toLocaleDateString()}</span>
                    )}
                  </div>
                  
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        backgroundColor: '#17a2b8',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    
                    {editorial.status === 'draft' && (
                      <button
                        onClick={() => handlePublishItems([editorial.id])}
                        disabled={isLoading}
                        style={{
                          flex: 1,
                          padding: '0.5rem',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.8rem'
                        }}
                      >
                        📢 Publish
                      </button>
                    )}
                    
                    <button
                      onClick={() => handleArchiveItems([editorial.id])}
                      disabled={isLoading}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      📦
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {filteredEditorials.length === 0 && (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem', 
              color: '#666',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0' }}>No editorials found</h3>
              <p style={{ margin: 0 }}>
                {activeTab === 'drafts' 
                  ? 'Create your first editorial or check your filters' 
                  : `No ${activeTab} editorials yet`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '6px',
          fontSize: '0.9rem'
        }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default EditorialCuration;